import queue from 'async/queue'
import retry from 'async/retry'
import fetch from 'isomorphic-fetch'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Uppy from '@uppy/core'
import * as Sentry from '@sentry/browser';

const ajaxQueue = queue((task, callback) => {
  if (task.onStartCb) {
    task.onStartCb()
  }
  task.cancelled = false
  task.running = true

  task.cancel = () => {
    task.cancelled = true
    if (task.xhr) {
      task.xhr.abort()
    }
  }

  const func = (c) => {
    if (task.cancelled) {
      c(new Error('abort'))
      return
    }

    const xhr = task.xhr
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        xhr.onerror = null
        if (xhr.status >= 200 && xhr.status < 400) {
          c(null, xhr.responseText ? JSON.parse(xhr.responseText) : '')
        } else {
          c(new Error('Network error: ', xhr.status))
        }
      }
    }
    xhr.onerror = () => {
      xhr.onreadystatechange = null
      if (!task.cancelled) {
        c(new Error('Unknown network error'))
      }
    }
    xhr.send(task.data)
  }

  retry({
    times: task.retries || 0,
    errorFilter: e => e.message !== 'abort',
  }, func, (err, result) => {
    callback(err, result)
  })
}, 2)

const pushToAjaxQueue = (task, callback) => {
  if (ajaxQueue.running() >= ajaxQueue.concurrency) {
    task.queued = true
  }
  ajaxQueue.push(task, callback)
}

export function transcode(options, callback) {
  fetch(`${process.env.REACT_APP_API_HOST}/transcode/${options.shortcode}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options),
    credentials: 'include',
  })
  .then(async (response) => {
    if (!response.ok) {
      throw Error(await response.text())
    }
    return response.json()
  })
  .then((json) => {
    if (json) { callback(null, json) }
  })
  .catch(error => {
    Sentry.captureException(error);
    callback(error)
  })
}

export function upload(file, options, uploadMetaData, callback, progressCb, startCb) {
  if (uploadMetaData['key']) {
    const cb = (error, data) => {
      if (error) {
        console.log("Attempting XHR, uppy failed.")
        xhrUpload(file, options, uploadMetaData, callback, progressCb, startCb)
        return
      }
      callback(error, data)
    }
    return s3upload(file, options, uploadMetaData, cb, progressCb, startCb)
  } else {
    return xhrUpload(file, options, uploadMetaData, callback, progressCb, startCb)
  }
}

export function s3upload(file, options, uploadMetaData, callback, progressCb, startCb) {
  const uppy = Uppy()
  const host = process.env.REACT_APP_UPLOADER_HOST
  const uploadStarted = Date.now()
  console.log("Loading companion host " + host)
  uppy.use(AwsS3Multipart, {
    limit: 4,
    companionUrl: host,
  })
  uppy.on('upload-progress', (file, progress) => {
    const timeElapsed = (new Date()) - uploadStarted
    const speed = progress.bytesUploaded / (timeElapsed / 1000)
    progressCb({
      percent: parseInt((progress.bytesUploaded / progress.bytesTotal) * 100, 10),
      speed: speed,
    })
  })

  const fileId = uppy.addFile({
    source: 'file input',
    name: uploadMetaData['key'],
    type: file.type,
    data: file, 
  })

  let retries = 2
  const doTry = (uploadReq) => {
    uploadReq
    .then((result) => {
      if (!result) return;
      console.info('Successful uploads:', result.successful)
    
      if (result.failed.length > 0) {
        callback(new Error(file.error || 'Network error. Please try again.'))
        return
      }
      const transcoderOptions = {
        ...options,
        ...uploadMetaData.transcoder_options,
      }
      transcode(transcoderOptions, (transcodeErr, data) => {
        callback(transcodeErr, data)
      })
    })
    .catch(error => {
      if (retries > 0) {
        console.log("Retrying.")
        retries = retries - 1
        doTry(uppy.retryUpload(fileId))
      } else {
        console.log("Failing.")
        Sentry.captureException(error);
        callback(error)
      }  
    })
  }

  doTry(uppy.upload())
  

  return {
    cancel: () => {
      uppy.cancelAll();
    },
    queued: false
  }
}

export function xhrUpload(file, options, uploadMetaData, callback, progressCb, startCb) {
  const formData = new FormData()
  // transcoding starts immediately!
  if (uploadMetaData.transcoder) {
    // add options to POST
    for (const key of Object.keys(options)) {
      formData.append(key, options[key])
    }
  }

  for (const fieldName of Object.keys(uploadMetaData.fields)) {
    formData.append(fieldName, uploadMetaData.fields[fieldName])
  }
  formData.append('file', file)

  const xhr = new window.XMLHttpRequest()
  let lastTime = Date.now()
  let lastLoaded = 0
  xhr.upload.addEventListener('progress', (evt) => {
    let speed = 0
    if (evt.loaded) {
      const timeDelta = Date.now() - lastTime
      const loadedDelta = evt.loaded - lastLoaded

      speed = (loadedDelta / timeDelta) * 1000

      lastLoaded = evt.loaded
      lastTime = Date.now()
    }
    if (evt.lengthComputable) {
      progressCb({
        percent: parseInt((evt.loaded / evt.total) * 100, 10),
        speed,
      })
    }
  }, false)
  xhr.open('post', uploadMetaData.url, true)

  const task = {
    xhr,
    data: formData,
    onStartCb: () => {
      task.queued = false
      if (startCb) {
        startCb()
      }
    },
  }

  pushToAjaxQueue(task, (err, result) => {
    if (task.cancelled) {
      return
    }
    if (err) {
      let errorThrown = err.message
      if (!errorThrown || errorThrown.length === 0) {
        errorThrown = 'Network error. Please try again.'
      }
      if (errorThrown === 'abort') {
        return
      }

      callback(new Error(errorThrown || 'Network error. Please try again.'))
      return
    }

    if (uploadMetaData.transcoder) {
      if (result === null) {
        callback(new Error('Unable to process file'))
      } else {
        callback(null, { job_id: result.job_id })
      }
    } else {
      const transcoderOptions = {
        ...options,
        ...uploadMetaData.transcoder_options,
      }
      transcode(transcoderOptions, (transcodeErr, data) => {
        callback(transcodeErr, data)
      })
    }
  })
  return task
}
